/**
 * @file preview.ts
 * @description Живой предпросмотр на канве — `commora:canvas-preview`
 * (phase19.16, партия SB120, этап 1; TD-403; проект данных
 * docs/phases/phase19.16-materials/db-design-sb120-stage1.md §5, проект UI
 * uiux-sb120-stage1.md §4). Кабинет шлёт значение ползунка не чаще кадра,
 * канва накладывает его на показанную секцию и перерисовывает только её —
 * без формы, без проверки перекрёстных правил, без шага отмены. Это не
 * хранение: настоящая правка приходит потом `block-draft`.
 *
 * Здесь одна чистая функция на обе стороны: белый список путей (только числа
 * и цвета оформления, колонок и фото) и проверка формы схемой. Путь — как у
 * `section-edit` (`columns.items.0.pad.top`); запись `[0]` тоже принимается.
 *
 * SB120 этап 3а (db-design-sb120-stage3 §10 п. 1): цвет и шрифт частей
 * (`…typo.<часть>.*`, в том числе у шапки раздела и надписей на фото),
 * отступы элемента px, место и плашка надписи на фото, заливка, рамка и
 * высота кнопки, пропорция плиток (тот же путь `aspect`, что у фото).
 *
 * SB120 этап 3б (добавочно): `perRow`, `cols`, `phoneLayout`, `layout`,
 * `shade`, `intervalSec`, `icon`, `head`, `stripe`, `divider.*`, `lines.*`;
 * `typo.*` новых видов — тем же путём частей (схема вида знает их ключи).
 */

import { cloneJson } from "../site-theme/repair.js";
import { sectionPayloadShapeSchema, type SectionPayload } from "./schema.js";

/** Почему предпросмотр не наложен (канва отвечает `canvas-preview-ack { ok: false }`). */
export type SectionPreviewFailure = "path_not_allowed" | "invalid_value";

export type SectionPreviewResult =
  | { ok: true; payload: SectionPayload }
  | { ok: false; reason: SectionPreviewFailure };

const SIDE = "(?:outerTop|outerBottom|innerTop|innerBottom|innerLeft|innerRight)";
/** Часть `typo` целиком или её поле (↺ снимает поле или часть). */
const TYPO = "typo\\.[a-z]+(?:\\.(?:color|font|weight|case|size|sizePhone|tracking))?";
const ELEMENT = "columns\\.items\\.\\d+\\.elements\\.\\d+";

/** Белый список путей предпросмотра (db-design-sb120-stage1 §5). */
export const SECTION_PREVIEW_PATHS: readonly RegExp[] = [
  // `outsideFrame` (SB120 этап 2): true — «Фон за рамку сайта», undefined — снять.
  /^look\.(?:container|outsideFrame|height\.value|heightPhone\.value|card\.(?:radius|shadow))$/,
  new RegExp(`^look\\.space\\.${SIDE}$`),
  new RegExp(`^look\\.space\\.phone\\.${SIDE}$`),
  /^look\.space\.(?:phoneFactor|overlap)$/,
  /^look\.bg\.(?:dim|fadeWidth|zoom|zoomPhone|focus|focusPhone|textColor|bleed\.(?:top|bottom))$/,
  /^look\.decor\.\d+\.(?:x|y|size|sizePhone)$/,
  /^columns\.(?:gap|elementGap|shares)$/,
  /^columns\.items\.\d+\.(?:bg|pad\.(?:top|bottom|left|right))$/,
  /^header\.(?:color|line\.width|line\.color)$/,
  // Фото: пропорции, высота «Числом» (ПК и телефон) и «По колонке, не меньше» (`height.min`).
  /^columns\.items\.\d+\.elements\.\d+\.(?:aspect|aspectPhone|height\.(?:value|valuePhone|min))$/,
  // SB120 этап 3а: части typo элемента, надписей на фото и шапки раздела.
  new RegExp(`^${ELEMENT}\\.${TYPO}$`),
  new RegExp(`^${ELEMENT}\\.overlays\\.\\d+\\.(?:x|y|plate|plate\\.(?:fill|density)|${TYPO})$`),
  new RegExp(`^header\\.${TYPO}$`),
  // Отступы элемента px; кнопка: заливка, рамка, высота (`height` числом — только у кнопки, иначе invalid_value).
  new RegExp(`^${ELEMENT}\\.space\\.(?:top|bottom|topPhone|bottomPhone)$`),
  new RegExp(`^${ELEMENT}\\.(?:fill|border|border\\.(?:width|color)|height)$`),
  // SB120 этап 3б: сетки (в ряд, на телефоне, вид), затемнение плиток, смена объявлений,
  // разделитель и значок вопросов, шапка, полосы и линии таблицы. Чужой вид — invalid_value по схеме.
  new RegExp(`^${ELEMENT}\\.(?:perRow|cols|phoneLayout|layout|shade|intervalSec|icon|head|stripe)$`),
  new RegExp(`^${ELEMENT}\\.(?:divider|lines)(?:\\.(?:width|color))?$`),
];

/** `look.decor[1].x` → `look.decor.1.x`. */
export function normalizeSectionPath(path: string): string {
  return path.replace(/\[(\d+)\]/g, ".$1");
}

/** Путь разрешён для предпросмотра. */
export function isSectionPreviewPath(path: string): boolean {
  const normalized = normalizeSectionPath(path);
  return SECTION_PREVIEW_PATHS.some((re) => re.test(normalized));
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Каким создаётся недостающий родитель-вариант. Высота телефона «как на ПК»
 * (нет `heightPhone`): ползунок числа тянет «Числом» — родитель
 * `{ kind: "number" }`. У `look.height` вид есть всегда: число у
 * «по содержимому» / «по фото» — `invalid_value`.
 */
const CREATED_PARENTS: Readonly<Record<string, () => Record<string, unknown>>> = {
  "look.heightPhone": () => ({ kind: "number" }),
};

/**
 * Родители с индексами в пути (этап 3б): разделитель вопросов и линии таблицы
 * без объекта создаются с толщиной 1 px — у них `width` обязателен, и цвет
 * без толщины иначе не прошёл бы схему. Кабинет шлёт объект целиком; это
 * страховка, чтобы предпросмотр не отказывал.
 */
const CREATED_PARENT_PATTERNS: ReadonlyArray<readonly [RegExp, () => Record<string, unknown>]> = [
  [new RegExp(`^${ELEMENT}\\.(?:divider|lines)$`), () => ({ width: 1 })],
];

/** Недостающий родитель по пути до него; нет записи — пустой объект. */
function createdParent(path: string): Record<string, unknown> {
  const exact = CREATED_PARENTS[path];
  if (exact) return exact();
  for (const [re, make] of CREATED_PARENT_PATTERNS) if (re.test(path)) return make();
  return {};
}

/**
 * Наложить значение на копию секции. `value: undefined` — снять ключ
 * (предпросмотр «↺ как у сайта»); `null` — откат (Esc, отпустили на исходном):
 * возвращается копия без изменений. Промежуточные объекты необязательных
 * полей создаются (`look.space.phone`, `columns.items.0.pad`; `look.heightPhone`
 * — как «Числом», `CREATED_PARENTS`); элементов
 * списков — нет: несуществующий рисунок, колонка или элемент — `invalid_value`.
 * Не прошло форму схемы — `invalid_value` (перекрёстные правила не
 * проверяются: при перетаскивании доли и так дают 12).
 */
export function applySectionPreview(payload: SectionPayload, path: string, value: unknown): SectionPreviewResult {
  if (!isSectionPreviewPath(path)) return { ok: false, reason: "path_not_allowed" };
  const copy = cloneJson(payload) as unknown as Record<string, unknown>;
  if (value === null) return { ok: true, payload: copy as unknown as SectionPayload };

  const keys = normalizeSectionPath(path).split(".");
  let node: unknown = copy;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i]!;
    if (Array.isArray(node)) {
      node = node[Number(key)];
      if (node === undefined) return { ok: false, reason: "invalid_value" };
      continue;
    }
    if (!isPlainObject(node)) return { ok: false, reason: "invalid_value" };
    // Список (рисунки, колонки, элементы) не создаём: его индекс должен существовать.
    if (node[key] === undefined) {
      if (/^\d+$/.test(keys[i + 1] ?? "")) return { ok: false, reason: "invalid_value" };
      // Снять ключ, которого нет, — показывать нечего: секция как есть.
      if (value === undefined) return { ok: true, payload: copy as unknown as SectionPayload };
      node[key] = createdParent(keys.slice(0, i + 1).join("."));
    }
    node = node[key];
  }
  const last = keys[keys.length - 1]!;
  if (!isPlainObject(node)) return { ok: false, reason: "invalid_value" };
  if (value === undefined) delete node[last];
  else node[last] = cloneJson(value);

  const parsed = sectionPayloadShapeSchema.safeParse(copy);
  return parsed.success ? { ok: true, payload: parsed.data } : { ok: false, reason: "invalid_value" };
}
